import { InterviewStage, RejectedReason } from '../types';

type DropdownOption = {
  label: string;
  value: string;
};

const isRejectedStage = (stage: InterviewStage): boolean =>
  stage.name.toLowerCase().includes('rejected');

// Surface any stage whose name mentions "rejected" first so recruiters land
// on the canonical reject target without scrolling. Stable within each
// group, so the upstream ATS's own ordering is preserved otherwise.
const sortStagesWithRejectedFirst = (
  stages: InterviewStage[]
): InterviewStage[] =>
  [...stages].sort((a, b) => {
    const aRejected = isRejectedStage(a);
    const bRejected = isRejectedStage(b);
    if (aRejected === bRejected) {
      return 0;
    }
    return aRejected ? -1 : 1;
  });

/**
 * Builds the options for the "reject candidate" dropdown.
 *
 * LM/Pinpoint on Kombo: provider has no working reject endpoint, so we
 * model rejection as a stage move into the ATS's "Rejected" stage. The
 * caller shows the stage picker, writes `atsInterviewStageId` instead of
 * `atsRejectedReasonId`, and the candidate-stream handler dispatches the
 * move (see `processShortlistedAtsCandidate.ts` in the integration service).
 */
export const getRejectionDropdownOptions = ({
  useStagesForRejection,
  interviewStages,
  rejectedReasons
}: {
  useStagesForRejection: boolean;
  interviewStages: InterviewStage[];
  rejectedReasons: RejectedReason[];
}): DropdownOption[] => {
  if (useStagesForRejection) {
    return sortStagesWithRejectedFirst(interviewStages).map((stage) => ({
      label: stage.name,
      value: stage.id
    }));
  }

  return rejectedReasons.map((reason) => ({
    label: reason.label,
    value: reason.id
  }));
};
